import { RootState } from '@Common/models/redux/store';
import {
  Box,
  Fade,
  Grid,
  IconButton,
  Slide,
  Typography,
  Close,
  makeStyles,
} from '@quillbot/design-system';
import Track from '@Common/handlers/track';
import TrackLogger from 'core/logger/track';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserProfile } from 'src/authentication/handlers/api';
import { updateUserAdditionalDetailsAction } from 'src/authentication/actions/authenticationActions';
import { User } from 'src/authentication/models/store';

const useStyles = makeStyles((theme) => ({
  npsContainer: {
    width: '400px',
    position: 'fixed',
    right: '16px',
    bottom: '8px',
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    padding: '16px',
    display: 'block',
    borderRadius: '6px',
  },
  npsText: {
    maxWidth: '260px',
    fontSize: '16px',
    padding: '4px 0px 12px',
  },
  closeButton: {
    float: 'right',
    padding: '4px',
    position: 'absolute',
    right: '8px',
    zIndex: 1001,
  },
  bottomText: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
  },
  npsOptions: {
    color: theme.palette.text.secondary,
    width: '28px',
    height: '29px',
    border: '1px solid',
    borderRadius: '4px',
    marginTop: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
    },
  },
  closingText: {
    zIndex: (showThankYou) => (showThankYou ? 1 : -1),
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
interface IProps {
  inputText: string;
  quillingStarted: boolean;
}

const NpsScoreModal = (props: IProps) => {
  const [showThankYou, setShowThankYou] = useState(false);
  const [showNps, setShowNps] = useState(false);
  const { inputText, quillingStarted } = props;
  const userState: User = useSelector((rootState: RootState) => rootState.user);
  const classes = useStyles(showThankYou);
  const dispatch = useDispatch();

  useEffect(() => {
    // Check if height is available for showing nps
    const paraphraserBox = document.getElementById('inOutContainer');
    const pphrBottomPos =
      paraphraserBox &&
      paraphraserBox?.offsetHeight + paraphraserBox?.offsetTop;
    const bottomScreenPos = window.scrollY + window.innerHeight;
    if (
      bottomScreenPos &&
      pphrBottomPos &&
      bottomScreenPos - pphrBottomPos < 190
    ) {
      setShowNps(false);
    } else if (
      // Rule: on quill and if user haven't seen it before
      quillingStarted &&
      !userState.shownNps &&
      typeof userState.shownNps === 'boolean'
    ) {
      setShowNps(true);
    }
  }, [inputText, quillingStarted]);

  useEffect(() => {
    if (showNps) {
      Track.npsActions(
        'pphr/user/shown_nps_modal_oct21',
        userState.counts?.submittedInputCount,
        userState.premium
      );
    }
  }, [showNps]);

  const handleSubmit = (value: number) => {
    TrackLogger.getInstance().ampSetOnce('nps_oct21', value);
    setTimeout(() => {
      setShowNps(false);
      updateUserProfile({
        uid: `${userState.uid}`,
        shownNps: true,
        npsScore: value,
      }).then(() =>
        dispatch(
          updateUserAdditionalDetailsAction({ ...userState, shownNps: true })
        )
      );
    }, 2000);
    setShowThankYou(true);
    Track.npsActions(
      'pphr/user/submitted_nps_oct21',
      userState.counts?.submittedInputCount,
      userState.premium,
      value,
      userState.creationTime
    );
  };

  const handleClose = () => {
    setShowNps(false);
    updateUserProfile({
      uid: `${userState.uid}`,
      shownNps: true,
    }).then(() =>
      dispatch(
        updateUserAdditionalDetailsAction({ ...userState, shownNps: true })
      )
    );
    Track.npsActions(
      'pphr/user/closed_nps_oct21',
      userState.counts?.submittedInputCount,
      userState.premium
    );
  };

  const getOptions = () =>
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
      <Grid
        onClick={() => handleSubmit(value)}
        key={value}
        className={classes.npsOptions}
      >
        {value}
      </Grid>
    ));

  return (
    <Slide direction="up" in={showNps} timeout={300}>
      <Box boxShadow={3} className={classes.npsContainer}>
        <>
          <Grid
            item
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Grid className={classes.npsText}>
              How likely are you to recommend QuillBot to a friend?
            </Grid>
            <IconButton
              onClick={handleClose}
              className={classes.closeButton}
              size="large"
            >
              <Close />
            </IconButton>
          </Grid>
          <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
            {getOptions()}
          </Grid>
          <Grid className={classes.bottomText}>
            <Typography style={{ fontSize: '12px' }} color="textSecondary">
              Not likely at all
            </Typography>
            <Typography style={{ fontSize: '12px' }} color="textSecondary">
              Extremely Likely
            </Typography>
          </Grid>
        </>
        <div className={classes.closingText}>
          <Fade in={showThankYou}>
            <Typography style={{ fontSize: '20px' }}>
              Thank you for your Feedback.
            </Typography>
          </Fade>
        </div>
      </Box>
    </Slide>
  );
};

export default NpsScoreModal;
